import { Dialog, DialogContent, List, ListItem, ListItemButton, ListItemText, ListSubheader } from '@mui/material';
import { FC, memo, useContext, useEffect, useState } from 'react';
import LicenseContext from 'src/contexts/LicenseContext';
import { PlanningsDeliveryEntity } from 'src/entities/PlanningsDelivery.entity';
import { PlanningsDriverEntity } from 'src/entities/PlanningsDriver.entity';
import { PlanningsTruckEntity } from 'src/entities/PlanningsTruck.entity';
import { estimationMethods, EstimationMethodVo } from 'src/vo/EstimationMethod.vo';

import EstimationByDeliveriesComponent from '../components/EstimationByDeliveries.component';

type Props = {
  estimationDialogIsOpen: boolean;
  estimationOnClose: () => void;
  estimationMethod: EstimationMethodVo;
  estimationMethodOnClick: (value: EstimationMethodVo) => void;
  deliveryEntities: PlanningsDeliveryEntity[];
  driverEntities: PlanningsDriverEntity[];
  truckEntities: PlanningsTruckEntity[];
  selectedDeliveryIds: number[];
}

const EstimationDialogPresenter: FC<Props> = memo(({
  estimationDialogIsOpen,
  estimationOnClose,
  estimationMethod,
  estimationMethodOnClick,
  deliveryEntities,
  driverEntities,
  truckEntities,
  selectedDeliveryIds,
}) => {
  const context = useContext(LicenseContext);

  const [driverCostYenPerHours, setDriverCostYenPerHours] = useState<number>();
  const [truckExpresswayFeeYenPerShift, setTruckExpresswayFeeYenPerShift] = useState<number>();
  const [truckFuelCostYenPerKm, setTruckFuelCostYenPerKm] = useState<number>();
  const [truckInsuranceFeeYenPerDay, setTruckInsuranceFeeYenPerDay] = useState<number>();
  const [truckRepairCostYenPerDay, setTruckRepairCostYenPerDay] = useState<number>();

  useEffect(() => {
    if (!context.config) return;

    setDriverCostYenPerHours(context.config.driver_cost_yen_per_hours);
    setTruckExpresswayFeeYenPerShift(context.config.truck_expressway_fee_yen_per_shift);
    setTruckFuelCostYenPerKm(context.config.truck_fuel_cost_yen_per_km);
    setTruckInsuranceFeeYenPerDay(context.config.truck_insurance_fee_yen_per_day);
    setTruckRepairCostYenPerDay(context.config.truck_repair_cost_yen_per_day);
  }, [context.config]);

  return (
    <Dialog
      open={estimationDialogIsOpen}
      onClose={estimationOnClose}
      fullScreen={!!estimationMethod}
    >
      {
        !estimationMethod ? (
          <List
            sx={{ width: '100%', maxWidth: 360, bgcolor: 'background.paper' }}
            component="nav"
            aria-labelledby="nested-list-subheader"
            subheader={(
              <ListSubheader component="div" id="nested-list-subheader">
                見積もり方法を選択してください
              </ListSubheader>
            )}
          >
            {estimationMethods.map((it) => (
              <ListItem
                disablePadding
                key={it}
              >
                <ListItemButton
                  onClick={() => {
                    estimationMethodOnClick(it);
                  }}
                >
                  <ListItemText primary={it} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        ) : (
          <DialogContent>
            <EstimationByDeliveriesComponent
              onClose={estimationOnClose}
              planningsDeliveryEntities={deliveryEntities.filter((it) => selectedDeliveryIds.includes(it.id))}
              planningsDriverEntities={driverEntities}
              planningsTruckEntities={truckEntities}
              defaultDriverCostYenPerSeconds={driverCostYenPerHours ? driverCostYenPerHours / (60 * 60) : 0}
              defaultTruckExpresswayFeeYenPerDay={truckExpresswayFeeYenPerShift || 0}
              defaultTruckFuelCostYenPerMm={truckFuelCostYenPerKm ? (truckFuelCostYenPerKm / 1000000) : 0}
              defaultTruckInsuranceFeeYenPerDay={truckInsuranceFeeYenPerDay || 0}
              defaultTruckRepairFeeYenPerDay={truckRepairCostYenPerDay || 0}
              presetEstimationMethod={estimationMethod}
              estimationMethods={estimationMethods}
            />
          </DialogContent>
        )
      }
    </Dialog>
  );
});

export default EstimationDialogPresenter;
